"use client";

import React from "react";

import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import type { TAGULAR_LOCATIONS } from "@/types/tagular";

export type PricingPeriodToggleValue = "1year" | "5year";

interface PricingPeriodToggleOption {
  value: PricingPeriodToggleValue;
  label: string;
  ariaLabel: string;
  trackingSuffix: string;
}

export interface PricingPeriodToggleProps {
  value: PricingPeriodToggleValue;
  onChange: (value: PricingPeriodToggleValue) => void;
  className?: string;
  elementName?: string;
  location?: TAGULAR_LOCATIONS;
  trackingNamePrefix?: string;
}

const PRICING_PERIOD_TOGGLE_OPTIONS: PricingPeriodToggleOption[] = [
  {
    value: "1year",
    label: "1-year pricing",
    ariaLabel: "Show 1-year pricing",
    trackingSuffix: "1Year",
  },
  {
    value: "5year",
    label: "5-year pricing",
    ariaLabel: "Show 5-year pricing",
    trackingSuffix: "5Year",
  },
];

const PricingPeriodToggle: React.FC<PricingPeriodToggleProps> = ({
  value,
  onChange,
  className = "",
  elementName,
  location = "productCards",
  trackingNamePrefix = "ctaButtonProductCards",
}) => {
  const monarchMetadata = useMonarchMetadata();

  return (
    <div
      className={`inline-flex rounded-lg border-2 btn-outline-neutral bg-neutral-100 p-2 ${className}`.trim()}
    >
      {PRICING_PERIOD_TOGGLE_OPTIONS.map((option) => (
        <button
          key={option.value}
          className={`cursor-pointer px-6 py-2 rounded-lg title-sm-semibold transition-colors ${
            value === option.value
              ? "bg-gradient-expressive text-white"
              : "text-neutral-700 hover:text-theme1-base"
          }`}
          onClick={() => {
            onChange(option.value);
            fireElementClicked({
              elementType: "ctaButton",
              text: option.label,
              name: elementName
                ? `${trackingNamePrefix}${elementName}PricingToggle${option.trackingSuffix}`
                : `${trackingNamePrefix}PricingToggle${option.trackingSuffix}`,
              location,
              ...(monarchMetadata && { monarchMetadata }),
            });
          }}
          aria-label={option.ariaLabel}
          aria-pressed={value === option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export default PricingPeriodToggle;
